import React, { useRef, Suspense, useState } from 'react';
import { SpotLight, Object3D } from 'three';
import lerp from 'lerp';
import { useFrame, useThree } from 'react-three-fiber';
import { MapControls, Sky } from 'drei';
import { objects as FOIObject } from '../types/objects';
import Loader from './Loader';
import Environment from './Environment';
import Totem from './Totem';
import Fields from './Fields';
import useZoomLevel from '../hooks/useZoomLevel';
import { WorldState } from '../App';
import Sideboards from './Sideboards';
import Tent from './PartyTent';
import Welcome from './Welcome';
import Characters from './Characters';
import Flag from './Flag';
import useLimitMovement from '../hooks/useLimitMovements';

type Props = {
  worldState: WorldState;
  isStarted: boolean;
  sunIntensity: number;
  sunHeight: number;
  displayPoleBeans: boolean;
  displayPopover: boolean;
  onVegetableClick: (name: string) => void;
  onObjectClick: (name: string) => void;
}

function getPos(tile: number) {
  return tile * 5;
}

function World({ worldState, isStarted, sunIntensity, sunHeight, displayPoleBeans, displayPopover, onVegetableClick, onObjectClick }: Props) {
  const { camera } = useThree();
  const mainLight = useRef<SpotLight>();
  const zoomLevel = useZoomLevel();
  const controls = useRef<MapControls>();;
  const [selected, setSelected] = useState<string | null>();
  useLimitMovement(controls.current, camera);

  const objectRefs:  { [key: string]: any }  = {
    tent: useRef<Object3D>(),
    totem: useRef<Object3D>(),
    peas: useRef<Object3D>(),
    sweetcorn: useRef<Object3D>(),
    beans: useRef<Object3D>(),
    babyleaf: useRef<Object3D>(),
    batavia: useRef<Object3D>(),
    butterhead: useRef<Object3D>(),
    bellagio: useRef<Object3D>(),
    iceberg: useRef<Object3D>(),
    romaine: useRef<Object3D>(),
    spinach: useRef<Object3D>(),
    cauliflower: useRef<Object3D>(),
    broccoli: useRef<Object3D>(),
    squash: useRef<Object3D>(),
    savoyCabbage: useRef<Object3D>(),
    chineseCabbage: useRef<Object3D>(),
    redCabbage: useRef<Object3D>(),
    whiteCabbage: useRef<Object3D>(),
    sprouts: useRef<Object3D>(),
    radish: useRef<Object3D>(),
    kohlrabi: useRef<Object3D>(),
    onion: useRef<Object3D>(),
  };

  useFrame(() => {
    if (mainLight && mainLight.current) {
      mainLight.current.position.set(
        camera.position.x + 10,
        camera.position.y + 10,
        camera.position.z + 10,
      );
    }

    if (zoomLevel === -1 && worldState === WorldState.COMPLETE) {
      camera.position.y = lerp(camera.position.y, 60, 0.06);
      camera.updateProjectionMatrix();
    }

    if (zoomLevel === 0 && worldState === WorldState.COMPLETE) {
      camera.position.y = lerp(camera.position.y, 45, 0.06);
      camera.updateProjectionMatrix();
    }

    if (zoomLevel === 1) {
      camera.position.y = lerp(camera.position.y, 30, 0.06);
      camera.updateProjectionMatrix();
    }

    if (zoomLevel === 2) {
      camera.position.y = lerp(camera.position.y, 20, 0.06);
      camera.updateProjectionMatrix();
    }
  });

  function handleObjectHover(name: FOIObject | null) {
    // update mouse cursor
    if (name)
      document.body.style.cursor = "pointer";
    else
    document.body.style.cursor = "default";

    // const newSelectedRef = name && objectRefs[name] && objectRefs[name].current ? objectRefs[name] : null;
    setSelected(name);
  }
  
  const shadowPos = 250;
  const shadowSize = 2;

  return (
    <>
      <hemisphereLight intensity={2} skyColor={0xffeeb1 as any} groundColor={0x080820 as any} />
      <directionalLight
        shadowCameraLeft={-shadowPos}
        shadowCameraRight={shadowPos}
        shadowCameraTop={shadowPos}
        shadowCameraBottom={-shadowPos}
        position={[shadowPos, sunHeight, shadowPos]}
        color={0xffffff as any}
        intensity={sunIntensity}
        castShadow
        shadow-mapSize-width={1024*shadowSize}
        shadow-mapSize-height={1024*shadowSize}
        shadowBias={-0.00001}
        shadowMapWidth={1024*shadowSize}
        shadowMapHeight={1024*shadowSize}
        shadowCameraFar={1000}
      /> 
      <ambientLight intensity={0.2} />
      <Suspense fallback={<Loader />}>
        <group name="World" position={[-150, 0, -80]}>
          <Environment />

          <Fields />
          <Totem
            ref={objectRefs.totem}
            isSelected={false}
            scale={[50, 50, 50]}
            position={[65, -1.2, 110]}
          />

          <Sideboards number={1} position={[60, 0, 5]} />
          <Sideboards number={3} position={[160, 0, 5]} />
          <Sideboards number={2} position={[310, 0, 5]} />
          
          <Sideboards number={6} position={[50, 0, 150]} rotation={[0, Math.PI, 0]} />
          <Sideboards number={9} position={[120, 0, 150]} rotation={[0, Math.PI, 0]} />
          <Sideboards number={5} position={[210, 0, 150]} rotation={[0, Math.PI, 0]} />
          <Sideboards number={7} position={[310, 0, 150]} rotation={[0, Math.PI, 0]} />

          <Sideboards number={10} position={[0, 0, 80]} rotation={[0, Math.PI/2, 0]} /> 

          <Tent
            ref={objectRefs.tent}
            isSelected={selected === 'tent'}
            onClick={onObjectClick.bind(null, 'tent')}
            onPointerOver={handleObjectHover.bind(null, 'tent')}
            onPointerOut={handleObjectHover.bind(null, null)}
            position={[355, 0, 110]}
            rotation={[0, -Math.PI/2, 0]}
            scale={[3, 3, 3]}
          /> 
          <Welcome
            position={[340, 0, 55]}
            rotation={[0, -Math.PI/2, 0]}
            scale={[3, 3, 3]}
          /> 

          <Flag
            model="002_Peas"
            ref={objectRefs.peas}
            isSelected={selected === 'peas'}
            onPointerOver={handleObjectHover.bind(null, 'peas')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'peas')}
            position={[getPos(9), 0, getPos(4) - 1]}
          />
         
          <Flag
            model="003_Sweetcorn"
            ref={objectRefs.sweetcorn}
            isSelected={selected === 'sweetcorn'}
            onPointerOver={handleObjectHover.bind(null, 'sweetcorn')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'sweetcorn')}
            position={[getPos(19.2), 0, getPos(12.2) - 1]}
          />
         
          <Flag
            model="002_Beans"
            ref={objectRefs.beans}
            isSelected={selected === 'beans'}
            onPointerOver={handleObjectHover.bind(null, 'beans')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'beans')}
            position={[getPos(9), 0, getPos(14) - 1]}
          />
          <Flag
            model="002_Babyleaf"
            ref={objectRefs.babyleaf}
            isSelected={selected === 'babyleaf'}
            onPointerOver={handleObjectHover.bind(null, 'babyleaf')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'babyleaf')}
            position={[getPos(21), 0, getPos(4) - 1]}
          />
        
          <Flag
            model="003_Batavia"
            ref={objectRefs.batavia}
            isSelected={selected === 'batavia'}
            onPointerOver={handleObjectHover.bind(null, 'batavia')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'batavia')}
            position={[getPos(21), 0, getPos(22) - 1]}
          />
          <Flag
            model="003_Butterhead"
            ref={objectRefs.butterhead}
            isSelected={selected === 'butterhead'}
            onPointerOver={handleObjectHover.bind(null, 'butterhead')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'butterhead')}
            position={[getPos(26), 0, getPos(22) - 1]}
          />
          <Flag
            model="002_Bellagio"
            ref={objectRefs.bellagio}
            isSelected={selected === 'bellagio'}
            onPointerOver={handleObjectHover.bind(null, 'bellagio')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'bellagio')}
            position={[getPos(31), 0, getPos(22) - 1]}
          />
          
          <Flag
            model="002_Iceberg"
            ref={objectRefs.iceberg}
            isSelected={selected === 'iceberg'}
            onPointerOver={handleObjectHover.bind(null, 'iceberg')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'iceberg')}
            position={[getPos(21), 0, getPos(23.9) - 1]}
          />

          <Flag
            model="002_Romaine"
            ref={objectRefs.romaine}
            isSelected={selected === 'romaine'}
            onPointerOver={handleObjectHover.bind(null, 'romaine')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'romaine')}
            position={[getPos(33), 0, getPos(4) - 1]}
          />
          
          <Flag
            model="002_Spinach"
            ref={objectRefs.spinach}
            isSelected={selected === 'spinach'}
            onPointerOver={handleObjectHover.bind(null, 'spinach')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'spinach')}
            position={[getPos(33), 0, getPos(16.7) - 1]}
          />
        
          <Flag
            model="003_Savoy"
            ref={objectRefs.savoyCabbage}
            isSelected={selected === 'savoyCabbage'}
            onPointerOver={handleObjectHover.bind(null, 'savoyCabbage')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'savoyCabbage')}
            position={[getPos(37.7), 0, getPos(4) - 1]}
          />
          <Flag
            model="003_Red_Cabbage"
            ref={objectRefs.redCabbage}
            isSelected={selected === 'redCabbage'}
            onPointerOver={handleObjectHover.bind(null, 'redCabbage')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'redCabbage')}
            position={[getPos(42.7), 0, getPos(4) - 1]}
          />
          <Flag
            model="003_Chinese_Cabbage"
            ref={objectRefs.chineseCabbage}
            isSelected={selected === 'chineseCabbage'}
            onPointerOver={handleObjectHover.bind(null, 'chineseCabbage')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'chineseCabbage')}
            position={[getPos(47.7), 0, getPos(4) - 1]}
          />
          
          <Flag
            model="003_White_Cabbage"
            ref={objectRefs.whiteCabbage}
            isSelected={selected === 'whiteCabbage'}
            onPointerOver={handleObjectHover.bind(null, 'whiteCabbage')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'whiteCabbage')}
            position={[getPos(37.7), 0, getPos(9) - 1]}
          />
          
          <Flag
            model="001_Sprouts"
            ref={objectRefs.sprouts}
            isSelected={selected === 'sprouts'}
            onPointerOver={handleObjectHover.bind(null, 'sprouts')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'sprouts')}
            position={[getPos(37.7), 0, getPos(19) - 1]}
          />

          <Flag
            model="001_Radish"
            ref={objectRefs.radish}
            isSelected={selected === 'radish'}
            onPointerOver={handleObjectHover.bind(null, 'radish')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'radish')}
            position={[getPos(49.6), 0, getPos(4) - 1]}
          />
          <Flag
            model="001_Kohlrabi"
            ref={objectRefs.kohlrabi}
            isSelected={selected === 'kohlrabi'}
            onPointerOver={handleObjectHover.bind(null, 'kohlrabi')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'kohlrabi')}
            position={[getPos(49.6), 0, getPos(14.3) - 1]}
          />
         
          <Flag
            model="001_Onion"
            ref={objectRefs.onion}
            isSelected={selected === 'onion'}
            onPointerOver={handleObjectHover.bind(null, 'onion')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'onion')}
            position={[getPos(49.6), 0, getPos(16.7) - 1]}
          />
         
          <Flag
            model="001_Squash"
            ref={objectRefs.squash}
            isSelected={selected === 'squash'}
            onPointerOver={handleObjectHover.bind(null, 'squash')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'squash')}
            position={[getPos(54.5), 0, getPos(4) - 1]}
          />
          
          <Flag
            model="001_Broccoli"
            ref={objectRefs.broccoli}
            isSelected={selected === 'broccoli'}
            onPointerOver={handleObjectHover.bind(null, 'broccoli')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'broccoli')}
            position={[getPos(54.5), 0, getPos(9) - 1]}
          />

          <Flag
            model="001_Cauliflower"
            ref={objectRefs.cauliflower}
            isSelected={selected === 'cauliflower'}
            onPointerOver={handleObjectHover.bind(null, 'cauliflower')}
            onPointerOut={handleObjectHover.bind(null, null)}
            onClick={onVegetableClick.bind(null, 'cauliflower')}
            position={[getPos(54.5), 0, getPos(14) - 1]}
          />

          <Characters
            isGuySelected={selected === 'character'}
            onGuyPointerOver={handleObjectHover.bind(null, 'character' )}
            onGuyPointerOut={handleObjectHover.bind(null, null)}
            onGuyClick={onObjectClick.bind(null, 'character')}
          />
        </group>
      </Suspense>

      <Sky />
      <MapControls
        ref={controls}
        enableRotate={false}
        enableZoom={false}
        // minDistance={50}
        // maxDistance={400}
        zoomSpeed={0.6}
        autoRotate={false}
      />
    </>
  );
}

export default World;
